import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
} from "react";

type Locale = "ja" | "en";

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const useLocale = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
};

interface LocaleProviderProps {
  children: ReactNode;
}

// Simple translation dictionary for demonstration
const translations: Record<Locale, Record<string, string>> = {
  ja: {
    "settings.title": "設定",
    "settings.account": "アカウント",
    "settings.profile": "プロフィール設定",
    "settings.security": "セキュリティ",
    "settings.appearance": "表示・一般",
    "settings.darkMode": "ダークモード",
    "settings.language": "言語",
    "settings.notifications": "通知",
    "settings.push": "プッシュ通知",
    "settings.email": "メール通知",
    "settings.about": "その他",
    "settings.aboutApp": "Arcanaについて",
    "settings.logout": "ログアウト",
    "common.save": "保存",
    "common.cancel": "キャンセル",
    "common.edit": "編集",
  },
  en: {
    "settings.title": "Settings",
    "settings.account": "Account",
    "settings.profile": "Profile Settings",
    "settings.security": "Security",
    "settings.appearance": "Appearance",
    "settings.darkMode": "Dark Mode",
    "settings.language": "Language",
    "settings.notifications": "Notifications",
    "settings.push": "Push Notifications",
    "settings.email": "Email Notifications",
    "settings.about": "About",
    "settings.aboutApp": "About Arcana",
    "settings.logout": "Logout",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.edit": "Edit",
  },
};

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const savedLocale = localStorage.getItem("app_locale");
    return savedLocale === "en" || savedLocale === "ja" ? savedLocale : "ja";
  });

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem("app_locale", newLocale);
  };

  const t = (key: string): string => {
    return translations[locale][key] || key;
  };

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LocaleContext.Provider>
  );
};
